// app_client/src/utils/pdfGenerator.js
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import moment from 'moment';

const cleanText = (str) => {
  if (!str) return '';
  const asciiOnly = String(str)
    .replace(/Ğ/g, 'G').replace(/ğ/g, 'g')
    .replace(/Ü/g, 'U').replace(/ü/g, 'u')
    .replace(/Ş/g, 'S').replace(/ş/g, 's')
    .replace(/İ/g, 'I').replace(/ı/g, 'i')
    .replace(/Ö/g, 'O').replace(/ö/g, 'o')
    .replace(/Ç/g, 'C').replace(/ç/g, 'c');

  return asciiOnly
    .split('')
    .filter((char) => char.charCodeAt(0) >= 0 && char.charCodeAt(0) <= 127)
    .join('');
};

const translations = {
  tr: {
    statementTitle: 'HESAP EKSTRESI',
    companyName: 'DOGANCAY INTERNATIONAL MOVERS',
    accountName: 'Cari Unvan',
    accountCode: 'Cari Kodu',
    dateRange: 'Tarih Araligi',
    createdAt: 'Olusturma Tarihi',
    currency: 'Para Birimi',
    page: 'Sayfa',
    tableDate: 'Tarih',
    tableDocument: 'Belge / Aciklama',
    tableAmount: 'Islem Dovizi / Tutar',
    tableDebit: 'Borc',
    tableCredit: 'Alacak',
    tableRunningBalance: 'Yuruyen Bakiye'
  },
  en: {
    statementTitle: 'ACCOUNT STATEMENT',
    companyName: 'DOGANCAY INTERNATIONAL MOVERS',
    accountName: 'Account Name',
    accountCode: 'Account Code',
    dateRange: 'Date Range',
    createdAt: 'Created At',
    currency: 'Currency',
    page: 'Page',
    tableDate: 'Date',
    tableDocument: 'Document / Description',
    tableAmount: 'Transaction Currency / Amount',
    tableDebit: 'Debit',
    tableCredit: 'Credit',
    tableRunningBalance: 'Running Balance'
  },
  de: {
    statementTitle: 'KONTOAUSZUG',
    companyName: 'DOGANCAY INTERNATIONAL MOVERS',
    accountName: 'Kontoname',
    accountCode: 'Kontocode',
    dateRange: 'Datumsbereich',
    createdAt: 'Erstellt am',
    currency: 'Waehrung',
    page: 'Seite',
    tableDate: 'Datum',
    tableDocument: 'Beleg / Beschreibung',
    tableAmount: 'Transaktionswaehrung / Betrag',
    tableDebit: 'Soll',
    tableCredit: 'Haben',
    tableRunningBalance: 'Laufender Saldo'
  }
};

const getPdfTranslations = (lang = 'tr') => (
  translations[String(lang || 'tr').trim().toLowerCase()] || translations.tr
);

function formatPdfCurrency(value, currency = 'TRY') {
  const amount = Number(value) || 0;
  const formattedAmount = new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
  return cleanText(`${formattedAmount} ${currency}`);
}

function slugifyAccountName(value = '') {
  return String(value || 'hesap')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'hesap';
}

function formatPdfDate(value, format = 'DD.MM.YYYY') {
  if (!value) return '-';
  const date = moment(value);
  return cleanText(date.isValid() ? date.format(format) : '-');
}

function buildPdfRows(rows = [], startDate = null, endDate = null) {
  const sortedRows = [...rows].sort((a, b) => {
    const dateDiff = new Date(a.date || a.createdAt || 0) - new Date(b.date || b.createdAt || 0);
    if (dateDiff !== 0) return dateDiff;
    return String(a.documentNo || a.sourceId || '').localeCompare(String(b.documentNo || b.sourceId || ''));
  });

  const startMoment = startDate ? moment(startDate).startOf('day') : null;
  const endMoment = endDate ? moment(endDate).endOf('day') : null;

  const filteredRows = sortedRows.filter((row) => {
    const rowMoment = moment(row.date || row.createdAt);
    if (!rowMoment.isValid()) return true;
    if (startMoment && rowMoment.isBefore(startMoment)) return false;
    if (endMoment && rowMoment.isAfter(endMoment)) return false;
    return true;
  });

  let runningBalance = 0;
  return filteredRows.map((row) => {
    const debit = Number(row.convertedDebit || 0);
    const credit = Number(row.convertedCredit || 0);
    runningBalance += debit - credit;

    return {
      ...row,
      runningBalance
    };
  });
}

export function exportConsolidatedAccountLedgerPDF(account, ledger = {}, lang = 'tr', startDate = null, endDate = null) {
  const labels = getPdfTranslations(lang);
  const selectedCurrency = ledger.reportCurrency || 'TRY';
  const doc = jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
  const rows = Array.isArray(ledger.rows) ? ledger.rows : [];
  const accountName = cleanText(account?.name || '-');
  const accountCode = cleanText(account?.code || '-');
  const currentDate = new Date();
  const currentDateLabel = cleanText(moment(currentDate).format('DD.MM.YYYY HH:mm'));
  const pdfStartDate = startDate || ledger?.dateRange?.startDate || ledger?.filters?.startDate || null;
  const pdfEndDate = endDate || ledger?.dateRange?.endDate || ledger?.filters?.endDate || null;
  const dateRangeLabel = cleanText(`${formatPdfDate(pdfStartDate)} - ${formatPdfDate(pdfEndDate)}`);
  const filenameStamp = moment(currentDate).format('YYYYMMDD_HHmm');
  const accountSlug = slugifyAccountName(accountName);
  const totalPagesExp = '{total_pages_count_string}';
  const pdfRows = buildPdfRows(rows, pdfStartDate, pdfEndDate);

  doc.setProperties({
    title: cleanText(labels.statementTitle),
    subject: cleanText(`${accountName} ${labels.statementTitle}`),
    author: cleanText(labels.companyName)
  });

  const drawHeader = (pageNumber) => {
    const leftX = 14;
    const rightX = 196;
    const headerBottom = 51;

    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, 210, 56, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(cleanText(labels.companyName), leftX, 16);

    doc.setFontSize(11);
    doc.text(cleanText(labels.statementTitle), leftX, 23);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(cleanText(`${labels.accountName}: ${accountName}`), leftX, 31);
    doc.text(cleanText(`${labels.accountCode}: ${accountCode}`), leftX, 38);
    doc.text(cleanText(`${labels.dateRange}: ${dateRangeLabel}`), leftX, 45);

    doc.text(cleanText(`${labels.createdAt}: ${currentDateLabel}`), rightX, 16, { align: 'right' });
    doc.text(cleanText(`${labels.currency}: ${selectedCurrency}`), rightX, 23, { align: 'right' });
    doc.text(cleanText(`${labels.page}: ${pageNumber}`), rightX, 30, { align: 'right' });

    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.2);
    doc.line(leftX, headerBottom, rightX, headerBottom);
  };

  const drawFooter = (pageNumber) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(14, pageHeight - 14, pageWidth - 14, pageHeight - 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(cleanText(labels.companyName), 14, pageHeight - 8);
    doc.text(cleanText(`${labels.page} ${pageNumber} / ${totalPagesExp}`), pageWidth - 14, pageHeight - 8, { align: 'right' });
  };

  const tableWidth = 182;
  const columnStyles = {
    0: { cellWidth: 22 },
    1: { cellWidth: 50 },
    2: { cellWidth: 35 },
    3: { cellWidth: 25, halign: 'right', noWrap: true },
    4: { cellWidth: 25, halign: 'right', noWrap: true },
    5: { cellWidth: 25, halign: 'right', noWrap: true }
  };

  autoTable(doc, {
    startY: 63,
    tableWidth,
    margin: { left: 14, right: 14 },
    head: [[
      cleanText(labels.tableDate),
      cleanText(labels.tableDocument),
      cleanText(labels.tableAmount),
      cleanText(labels.tableDebit),
      cleanText(labels.tableCredit),
      cleanText(labels.tableRunningBalance)
    ]],
    body: pdfRows.map((row) => ([
      cleanText(row.date ? moment(row.date).format('DD.MM.YYYY') : '-'),
      cleanText([
        row.documentNo || '-',
        row.description || row.category || row.type || '-'
      ].join('\n')),
      cleanText(row.originalAmountLabel || '-'),
      cleanText(row.convertedDebit > 0 ? formatPdfCurrency(row.convertedDebit, selectedCurrency) : '-'),
      cleanText(row.convertedCredit > 0 ? formatPdfCurrency(row.convertedCredit, selectedCurrency) : '-'),
      cleanText(formatPdfCurrency(row.runningBalance || 0, selectedCurrency))
    ])),
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      font: 'helvetica',
      halign: 'center',
      valign: 'middle'
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    styles: {
      font: 'helvetica',
      fontSize: 8,
      cellPadding: 2,
      valign: 'middle',
      overflow: 'linebreak'
    },
    columnStyles,
    didDrawPage: (data) => {
      drawHeader(data.pageNumber);
      drawFooter(data.pageNumber);
    }
  });

  if (typeof doc.putTotalPages === 'function') {
    doc.putTotalPages(totalPagesExp);
  }
  doc.save(`${accountSlug}-hesapekstresi-${filenameStamp}.pdf`);
}

const profitabilityTranslations = {
  tr: {
    statementTitle: 'KARLILIK ANALIZI',
    operationName: 'Operasyon',
    operationCode: 'Proje No',
    tableType: 'Islem Turu',
    tableAmount: 'Tutar',
    tableRunningBalance: 'Yuruyen Bakiye'
  },
  en: {
    statementTitle: 'PROFITABILITY ANALYSIS',
    operationName: 'Operation',
    operationCode: 'Project No',
    tableType: 'Type',
    tableAmount: 'Amount',
    tableRunningBalance: 'Running Balance'
  },
  de: {
    statementTitle: 'RENTABILITAETSANALYSE',
    operationName: 'Vorgang',
    operationCode: 'Projektnr.',
    tableType: 'Typ',
    tableAmount: 'Betrag',
    tableRunningBalance: 'Laufender Saldo'
  }
};

export function exportOperationProfitabilityPDF(operation = {}, ledger = {}, lang = 'tr', startDate = null, endDate = null) {
  const base = getPdfTranslations(lang);
  const extra = profitabilityTranslations[String(lang || 'tr').trim().toLowerCase()] || profitabilityTranslations.tr;
  const labels = { ...base, ...extra };
  const selectedCurrency = ledger.reportCurrency || 'TRY';
  const doc = jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
  const rows = Array.isArray(ledger.rows) ? ledger.rows : [];
  const operationName = cleanText(operation?.name || operation?.title || '-');
  const operationCode = cleanText(operation?.projectNo || operation?.operationCode || operation?.code || '-');
  const currentDate = new Date();
  const currentDateLabel = cleanText(moment(currentDate).format('DD.MM.YYYY HH:mm'));
  const dateRangeLabel = cleanText(`${formatPdfDate(startDate)} - ${formatPdfDate(endDate)}`);
  const filenameStamp = moment(currentDate).format('YYYYMMDD_HHmm');
  const fileSlug = slugifyAccountName(operationCode !== '-' ? operationCode : operationName);
  const totalPagesExp = '{total_pages_count_string}';

  const startMoment = startDate ? moment(startDate).startOf('day') : null;
  const endMoment = endDate ? moment(endDate).endOf('day') : null;
  const pdfRows = rows.filter((row) => {
    const rowMoment = moment(row.date || row.createdAt);
    if (!rowMoment.isValid()) return true;
    if (startMoment && rowMoment.isBefore(startMoment)) return false;
    if (endMoment && rowMoment.isAfter(endMoment)) return false;
    return true;
  });

  doc.setProperties({
    title: cleanText(labels.statementTitle),
    subject: cleanText(`${operationName} ${labels.statementTitle}`),
    author: cleanText(labels.companyName)
  });

  const drawHeader = (pageNumber) => {
    const leftX = 14;
    const rightX = 196;
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, 210, 56, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(cleanText(labels.companyName), leftX, 16);
    doc.setFontSize(11);
    doc.text(cleanText(labels.statementTitle), leftX, 23);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(cleanText(`${labels.operationName}: ${operationName}`), leftX, 31);
    doc.text(cleanText(`${labels.operationCode}: ${operationCode}`), leftX, 38);
    doc.text(cleanText(`${labels.dateRange}: ${dateRangeLabel}`), leftX, 45);
    doc.text(cleanText(`${labels.createdAt}: ${currentDateLabel}`), rightX, 16, { align: 'right' });
    doc.text(cleanText(`${labels.currency}: ${selectedCurrency}`), rightX, 23, { align: 'right' });
    doc.text(cleanText(`${labels.page}: ${pageNumber}`), rightX, 30, { align: 'right' });
  };

  const drawFooter = (pageNumber) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(14, pageHeight - 14, pageWidth - 14, pageHeight - 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(cleanText(labels.companyName), 14, pageHeight - 8);
    doc.text(cleanText(`${labels.page} ${pageNumber} / ${totalPagesExp}`), pageWidth - 14, pageHeight - 8, { align: 'right' });
  };

  autoTable(doc, {
    startY: 63,
    tableWidth: 182,
    margin: { left: 14, right: 14 },
    head: [[
      cleanText(labels.tableDate),
      cleanText(labels.tableType),
      cleanText(labels.tableDocument),
      cleanText(labels.tableAmount),
      cleanText(labels.tableRunningBalance)
    ]],
    body: pdfRows.map((row) => ([
      cleanText(row.date ? moment(row.date).format('DD.MM.YYYY') : '-'),
      cleanText(row.typeLabel || row.flowLabel || '-'),
      cleanText(row.documentLabel || '-'),
      cleanText(formatPdfCurrency(row.convertedAmount || 0, selectedCurrency)),
      cleanText(formatPdfCurrency(row.runningBalance || 0, selectedCurrency))
    ])),
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      font: 'helvetica',
      halign: 'center',
      valign: 'middle'
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    styles: {
      font: 'helvetica',
      fontSize: 8,
      cellPadding: 2,
      valign: 'middle',
      overflow: 'linebreak'
    },
    columnStyles: {
      0: { cellWidth: 24 },
      1: { cellWidth: 36 },
      2: { cellWidth: 52 },
      3: { cellWidth: 35, halign: 'right' },
      4: { cellWidth: 35, halign: 'right' }
    },
    didDrawPage: (data) => {
      drawHeader(data.pageNumber);
      drawFooter(data.pageNumber);
    }
  });

  if (typeof doc.putTotalPages === 'function') {
    doc.putTotalPages(totalPagesExp);
  }
  doc.save(`${fileSlug}-karlilik-${filenameStamp}.pdf`);
}

